import { baseApi } from './baseApi';
import {
  CashRegisterDto,
  CreateCashRegisterDto,
  CloseCashRegisterDto,
  CashMovementDto,
  CreateCashMovementDto,
  CashRegisterReconciliationDto,
  CashRegisterValidationDto,
  CashRegisterQueryDto,
  CashRegisterRestaurantStatus,
} from '../types/cashRegister';

export const cashRegisterApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Cash Register CRUD operations
    getCashRegisters: builder.query<CashRegisterDto[], { restaurantId?: string; query?: CashRegisterQueryDto }>({
      query: ({ query = {} }) => ({
        url: `/cash-registers/history`,
        params: query,
      }),
      providesTags: (result) => [
        { type: 'CashRegister' as const, id: 'LIST' },
        ...(result || []).map(({ id }) => ({ type: 'CashRegister' as const, id })),
      ],
    }),

    getCashRegister: builder.query<CashRegisterDto, string>({
      query: (registerId) => `/cash-registers/${registerId}`,
      providesTags: (result, error, registerId) => [
        { type: 'CashRegister' as const, id: registerId },
      ],
    }),

    getActiveCashRegisters: builder.query<CashRegisterDto | null, void>({
      query: () => `/cash-registers/active`,
      providesTags: (result) => [
        { type: 'CashRegister' as const, id: result?.id || 'ACTIVE' },
        { type: 'CashRegister' as const, id: 'ACTIVE' },
      ],
    }),

    // Get all open cash registers (Admin only)
    getAllOpenCashRegisters: builder.query<CashRegisterDto[], void>({
      query: () => `/cash-registers/all-open`,
      providesTags: (result) => [
        { type: 'CashRegister' as const, id: 'ALL_OPEN' },
        ...(result || []).map(({ id }) => ({ type: 'CashRegister' as const, id })),
      ],
    }),

    // History endpoint (alias for getCashRegisters with better name)
    getCashRegisterHistory: builder.query<CashRegisterDto[], { query?: CashRegisterQueryDto }>({
      query: ({ query = {} }) => ({
        url: `/cash-registers/history`,
        params: query,
      }),
      providesTags: (result) => [
        { type: 'CashRegister' as const, id: 'HISTORY' },
        ...(result || []).map(({ id }) => ({ type: 'CashRegister' as const, id })),
      ],
    }),

    // Cash Register operations
    openCashRegister: builder.mutation<CashRegisterDto, CreateCashRegisterDto>({
      query: (data) => ({
        url: `/cash-registers/open`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'CashRegister' as const, id: 'LIST' },
        { type: 'CashRegister' as const, id: 'ACTIVE' },
        { type: 'CashRegister' as const, id: 'RESTAURANT_STATUS' },
        { type: 'Analytics' as const, id: 'PARTIAL' },
        // El checklist de primeros pasos tacha "Abre tu caja" sin recargar.
        'Onboarding' as const,
      ],
    }),

    closeCashRegister: builder.mutation<CashRegisterDto, { registerId: string; data: CloseCashRegisterDto }>({
      query: ({ registerId, data }) => ({
        url: `/cash-registers/${registerId}/close`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { registerId }) => [
        { type: 'CashRegister' as const, id: 'LIST' },
        { type: 'CashRegister' as const, id: 'ACTIVE' },
        { type: 'CashRegister' as const, id: 'RESTAURANT_STATUS' },
        { type: 'CashRegister' as const, id: registerId },
        { type: 'Analytics' as const, id: 'PARTIAL' },
      ],
    }),

    // Cash Movements
    getCashMovements: builder.query<CashMovementDto[], string>({
      query: (registerId) => `/cash-registers/${registerId}/movements`,
      providesTags: (result, error, registerId) => [
        { type: 'CashMovement' as const, id: 'LIST' },
        { type: 'CashRegister' as const, id: registerId },
      ],
    }),

    addCashMovement: builder.mutation<CashMovementDto, { registerId: string; data: CreateCashMovementDto }>({
      query: ({ registerId, data }) => ({
        url: `/cash-registers/${registerId}/movements`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { registerId }) => [
        { type: 'CashMovement' as const, id: 'LIST' },
        { type: 'CashRegister' as const, id: registerId },
        { type: 'Analytics' as const, id: 'PARTIAL' },
      ],
    }),

    // Reconciliation (not implemented in backend yet)
    reconcileCashRegister: builder.mutation<
      CashRegisterReconciliationDto,
      { registerId: string; actualAmount: number }
    >({
      query: ({ registerId, actualAmount }) => ({
        url: `/cash-registers/${registerId}/reconcile`,
        method: 'POST',
        body: { actualAmount },
      }),
      invalidatesTags: (result, error, { registerId }) => [
        { type: 'CashRegister' as const, id: registerId },
      ],
    }),

    // Validation
    validateOpenCashRegister: builder.query<CashRegisterValidationDto, void>({
      query: () => `/cash-registers/validate-open`,
      providesTags: () => [
        { type: 'CashRegister' as const, id: 'VALIDATION' },
      ],
    }),

    validateCloseCashRegister: builder.query<CashRegisterValidationDto, string>({
      query: (registerId) => `/cash-registers/${registerId}/validate-close`,
      providesTags: (result, error, registerId) => [
        { type: 'CashRegister' as const, id: `validate-${registerId}` },
      ],
    }),

    // Check if user has active register
    hasActiveCashRegister: builder.query<boolean, void>({
      query: () => `/cash-registers/has-active`,
      providesTags: () => [
        { type: 'CashRegister' as const, id: 'HAS_ACTIVE' },
      ],
    }),

    // Summary endpoints
    getCashRegisterSummary: builder.query<any, string>({
      query: (registerId) => `/cash-registers/${registerId}/summary`,
      providesTags: (result, error, registerId) => [
        { type: 'CashRegister' as const, id: `summary-${registerId}` },
      ],
    }),

    getDailySummary: builder.query<any, { date?: string }>({
      query: ({ date }) => ({
        url: `/cash-registers/daily-summary`,
        params: { date },
      }),
      providesTags: () => [
        { type: 'CashRegister' as const, id: 'DAILY_SUMMARY' },
      ],
    }),

    getExpectedAmount: builder.query<number, string>({
      query: (registerId) => `/cash-registers/${registerId}/expected-amount`,
      providesTags: (result, error, registerId) => [
        { type: 'CashRegister' as const, id: `expected-${registerId}` },
      ],
    }),

    // Real-time polling for active register
    getActiveCashRegisterPolling: builder.query<CashRegisterDto, void>({
      query: () => `/cash-registers/active`,
      providesTags: () => [
        { type: 'CashRegister' as const, id: 'ACTIVE_POLLING' },
      ],
    }),

    // Restaurant-wide status for the proactive "no cash register" alert
    getRestaurantCashStatus: builder.query<CashRegisterRestaurantStatus, void>({
      query: () => `/cash-registers/restaurant-status`,
      providesTags: () => [
        { type: 'CashRegister' as const, id: 'RESTAURANT_STATUS' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCashRegistersQuery,
  useGetCashRegisterQuery,
  useGetActiveCashRegistersQuery,
  useGetAllOpenCashRegistersQuery,
  useGetCashRegisterHistoryQuery,
  useOpenCashRegisterMutation,
  useCloseCashRegisterMutation,
  useGetCashMovementsQuery,
  useAddCashMovementMutation,
  useReconcileCashRegisterMutation,
  useValidateOpenCashRegisterQuery,
  useValidateCloseCashRegisterQuery,
  useHasActiveCashRegisterQuery,
  useGetCashRegisterSummaryQuery,
  useGetDailySummaryQuery,
  useGetExpectedAmountQuery,

  // Polling hooks
  useGetActiveCashRegisterPollingQuery,
  useGetRestaurantCashStatusQuery,
} = cashRegisterApi;